/* Hallmark component scope: inherited tokens; pre-emit P4 H4 E4 S5 R5 V3. */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check, Maximize, RefreshCw, X } from 'lucide-react'
import { api } from '../../utils/api'
import { PPM, useEditorStore, type RowConfig } from '../../store/editorStore'
import type { Draft } from '../../hooks/useDataCenterApi'
import { placementFromSource } from '../../utils/placement.logic'
import { FloorReviewMap } from './FloorReviewMap'
import { frameFor, sceneObjects, valueText, type Change as MapChange, type Frame, type Snapshot } from './floorReview.logic'
import './FloorReviewPanel.css'
import './FillMissingFields.css'

export type SaveDraft = () => Promise<Draft | null | undefined>
type Values = { width_m?: number; depth_m?: number; height_m?: number; tenant?: string; max_power_w?: number }
type Change = { rack_id: string; label: string; before: Values; after: Values; preserved: Values }
type Movement = { rack_id: string; label: string; before: { x_m: number; y_m: number }; after: { x_m: number; y_m: number } }
type Placement = { changes: Movement[]; row_updates: Record<string, RowConfig> }
type GeometryPreview = Placement & { can_apply: boolean; issues: string[]; before: Snapshot; after: Snapshot;
  estimates: { rack_id: string; width_m: number; depth_m: number; fields: string[] }[] }
type Preview = { revision: number; preview_token: string; source: string; placement_changed: boolean;
  counts: { selected: number; changed_racks: number; filled_fields: number; preserved_fields: number }; changes: Change[]; placement?: GeometryPreview | null }
type Request = { dc_code: string; draft_id: number; expected_revision: number; rack_ids: string[]; values: Values }
const fields = [
  { key: 'width_m', label: 'Ширина W, м', unit: 'м', type: 'number' },
  { key: 'depth_m', label: 'Глубина D, м', unit: 'м', type: 'number' },
  { key: 'height_m', label: 'Физическая высота, м', unit: 'м', type: 'number' },
  { key: 'tenant', label: 'Арендатор', unit: '', type: 'text' },
  { key: 'max_power_w', label: 'Лимит мощности, Вт', unit: 'Вт', type: 'number' },
] as const
function errorText(cause: unknown) {
  const error = cause as { response?: { data?: { detail?: unknown } }; message?: string }
  return typeof error.response?.data?.detail === 'string' ? error.response.data.detail : error.message || 'Операция не выполнена. Повторите проверку.'
}

export function FillMissingFields({ dcCode, saveDraft, onClose }: { dcCode: string; saveDraft: SaveDraft; onClose: () => void }) {
  const dialog = useRef<HTMLElement>(null)
  const queryClient = useQueryClient()
  const racks = Object.values(useEditorStore(state => state.racks))
  const [selected, setSelected] = useState(() => useEditorStore.getState().selectedIds.filter(id => racks.some(rack => rack.id === id)))
  const [filter, setFilter] = useState('')
  const [input, setInput] = useState<Record<string, string>>({})
  const [prepared, setPrepared] = useState<{ request: Request; localIds: Record<string, string>; report: Preview } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [side, setSide] = useState<'before' | 'after'>('before')
  const [frame, setFrame] = useState<Frame>({ x: 0, y: 0, w: 4, h: 4 })
  const [activeObject, setActiveObject] = useState<string | null>(null)
  const [pathway, setPathway] = useState(true)
  const operationKey = useRef('fill-' + crypto.randomUUID())
  const visible = racks.filter(rack => (rack.label + ' ' + rack.id).toLocaleLowerCase().includes(filter.toLocaleLowerCase()))

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const root = document.getElementById('root')
    const wasInert = root?.hasAttribute('inert')
    root?.setAttribute('inert', '')
    dialog.current?.querySelector<HTMLButtonElement>('button')?.focus()
    return () => {
      if (!wasInert) root?.removeAttribute('inert')
      if (previous?.isConnected) previous.focus()
    }
  }, [])

  useEffect(() => {
    if (!busy) dialog.current?.querySelector<HTMLButtonElement>('button')?.focus()
  }, [prepared, busy])

  const preview = async () => {
    setBusy(true); setError(''); setPrepared(null)
    try {
      const values: Values = {}
      for (const field of fields) {
        const text = (input[field.key] || '').trim()
        if (!text) continue
        if (field.key === 'tenant') values.tenant = text
        else {
          const value = Number(text)
          if (!Number.isFinite(value) || (field.key === 'max_power_w' ? value < 0 : value <= 0)) throw new Error('Размеры должны быть положительными, лимит мощности — неотрицательным.')
          values[field.key] = value
        }
      }
      if (!selected.length || !Object.keys(values).length) throw new Error('Выберите стойки и укажите хотя бы одно значение.')
      const saved = await saveDraft()
      if (!saved) throw new Error('Не удалось сохранить текущий план. Заполнение не подготовлено.')
      const current = useEditorStore.getState()
      const localIds: Record<string, string> = {}
      for (const rack of Object.values(current.racks)) {
        const index = (saved.racks_json?.added || []).findIndex((item: { row_index: number; rack_index: number }) => item.row_index === rack.rowIndex && item.rack_index === rack.rackIndex)
        const key = rack.dbId != null ? String(rack.dbId) : index >= 0 ? 'added:' + index : null
        if (!key || localIds[key]) throw new Error('Стойка не имеет однозначной идентичности в сохранённом плане.')
        localIds[key] = rack.id
      }
      const rackIds = selected.map(id => Object.keys(localIds).find(key => localIds[key] === id))
      if (rackIds.some(key => !key)) throw new Error('Состав стоек изменился; откройте заполнение заново.')
      const request: Request = { dc_code: dcCode, draft_id: saved.id, expected_revision: saved.revision, rack_ids: rackIds as string[], values }
      const report = (await api.post<Preview>('/api/floor-workbench/missing/preview', request)).data
      if (report.placement) {
        const affected = new Set([...report.changes, ...report.placement.changes].map(change => 'rack:' + change.rack_id))
        const affectedRows = new Set([...affected].map(key => report.placement!.after.floor.racks[key.slice(5)]?.row_id).filter(Boolean))
        const focusObjects = [report.placement.before, report.placement.after].flatMap(snapshot => sceneObjects(snapshot).map(object => {
          const estimate = report.placement!.estimates.find(item => object.key === 'rack:' + item.rack_id)
          if (!estimate || !object.rect) return object
          const rack = snapshot.floor.racks[estimate.rack_id]
          return { ...object, rect: { ...object.rect, w: rack.width_m ?? estimate.width_m, h: rack.depth_m ?? estimate.depth_m } }
        }).filter(object => affected.has(object.key) || object.kind === 'rack' && affectedRows.has(snapshot.floor.racks[object.key.slice(5)]?.row_id)))
        setFrame(frameFor(focusObjects))
      }
      setSide('before'); setActiveObject(null)
      operationKey.current = 'fill-' + crypto.randomUUID()
      setPrepared({ request, localIds, report })
    } catch (cause) { setError(errorText(cause)) }
    finally { setBusy(false) }
  }

  const apply = async () => {
    if (!prepared || prepared.report.placement?.can_apply === false) return
    setBusy(true); setError('')
    try {
      const state = useEditorStore.getState()
      if (['dirty', 'saving', 'error'].includes(state.saveStatus)) throw new Error('Локальный план изменился; вернитесь к вводу и повторите проверку.')
      const result = (await api.post<{ revision: number; changes: Change[]; placement?: Placement | null }>('/api/floor-workbench/missing/apply', {
        ...prepared.request, preview_token: prepared.report.preview_token, operation_key: operationKey.current,
      })).data
      const saved = (await api.get<Draft>(`/api/datacenters/${dcCode}/drafts/${prepared.request.draft_id}`)).data
      if (saved.revision !== result.revision) throw new Error('Заполнение сохранено, но черновик уже изменён другим оператором. Перезагрузите план перед следующей правкой.')
      const next = { ...useEditorStore.getState().racks }
      for (const change of result.changes) {
        const id = prepared.localIds[change.rack_id]
        if (!id || !next[id]) throw new Error('Черновик сохранён, но состав локальных стоек изменился. Перезагрузите план.')
        const patch = change.after
        next[id] = { ...next[id],
          ...(patch.height_m !== undefined ? { heightM: patch.height_m } : {}),
          ...(patch.tenant !== undefined ? { tenant: patch.tenant } : {}),
          ...(patch.max_power_w !== undefined ? { max_power_w: patch.max_power_w } : {}), isDirty: false }
        if (patch.width_m !== undefined) { next[id].widthM = patch.width_m; next[id].width = patch.width_m * PPM }
        if (patch.depth_m !== undefined) { next[id].depthM = patch.depth_m; next[id].height = patch.depth_m * PPM }
        next[id].placement = placementFromSource(next[id])
      }
      for (const movement of result.placement?.changes || []) {
        const id = prepared.localIds[movement.rack_id]
        if (!id || !next[id]) throw new Error('Черновик сохранён, но зависимая стойка не найдена. Перезагрузите план.')
        const rack = { ...next[id], x: movement.after.x_m * PPM, y: movement.after.y_m * PPM, isDirty: false }
        next[id] = { ...rack, placement: placementFromSource(rack) }
      }
      const rows = { ...useEditorStore.getState().rows }
      for (const [id, row] of Object.entries(result.placement?.row_updates || {})) rows[id] = { ...rows[id], ...row, id }
      useEditorStore.setState({ racks: next, rows, baselineRacks: structuredClone(next), saveStatus: 'saved' })
      useEditorStore.getState().pushHistory()
      queryClient.setQueryData<Draft[]>(['drafts', dcCode], current => current?.map(draft => draft.id === saved.id ? saved : draft))
      onClose()
    } catch (cause) { setError(errorText(cause)) }
    finally { setBusy(false) }
  }

  const geometry = prepared?.report.placement
  const snapshot = geometry?.[side]
  const objects = snapshot ? sceneObjects(snapshot).map(object => {
    const estimate = geometry?.estimates.find(item => object.key === 'rack:' + item.rack_id)
    if (!estimate || !object.rect) return object
    const rack = snapshot.floor.racks[estimate.rack_id]
    return { ...object, rect: { ...object.rect, w: rack.width_m ?? estimate.width_m, h: rack.depth_m ?? estimate.depth_m } }
  }) : []
  const mapChanges: MapChange[] = geometry && prepared ? [...new Set([...prepared.report.changes, ...geometry.changes].map(change => change.rack_id))].map(id => ({
    object_type: 'rack', object_id: id, change: geometry.changes.some(item => item.rack_id === id) ? 'moved' : 'changed', fields: [],
    before: geometry.before.floor.racks[id], after: geometry.after.floor.racks[id],
  })) : []

  return createPortal(<div className="fill-fields-backdrop">
    <section ref={dialog} className="fill-fields" role="dialog" aria-modal="true" aria-label="Заполнение пропусков" aria-busy={busy}
      onKeyDown={event => {
        event.stopPropagation()
        if (event.key === 'Escape' && !busy) { event.preventDefault(); onClose() }
        if (event.key === 'Tab') {
          const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), summary')]
          const last = controls[controls.length - 1]
          if (event.shiftKey && document.activeElement === controls[0]) { event.preventDefault(); last?.focus() }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); controls[0]?.focus() }
        }
      }}>
      <header><div><h2>Заполнение пропусков</h2><span>{dcCode} · текущий черновик</span></div><button disabled={busy} onClick={onClose} aria-label="Закрыть заполнение" title="Закрыть заполнение"><X size={18} /></button></header>
      <main>
        {!prepared ? <div className="fill-fields-input">
          <fieldset disabled={busy}><legend>Стойки · выбрано {selected.length}</legend>
            <label>Найти стойку<input value={filter} onChange={event => setFilter(event.target.value)} type="search" /></label>
            <div className="fill-fields-selection">
              {visible.map(rack => <label key={rack.id}><input type="checkbox" checked={selected.includes(rack.id)} onChange={event => setSelected(current => event.target.checked ? [...current, rack.id] : current.filter(id => id !== rack.id))} />
                <span>{rack.label}<small>{rack.dbId != null ? 'ID ' + rack.dbId : 'Новая стойка'}</small></span></label>)}
              {!visible.length && <p>Стойки не найдены</p>}
            </div>
          </fieldset>
          <fieldset disabled={busy}><legend>Значения для пустых полей</legend>
            {fields.map(field => <label key={field.key}>{field.label}<input type={field.type} value={input[field.key] || ''}
              step={field.type === 'number' ? 'any' : undefined} min={0}
              onChange={event => setInput(current => ({ ...current, [field.key]: event.target.value }))} /></label>)}
          </fieldset>
        </div> : <>
          <div className="fill-fields-summary" role="status"><strong>Полей к заполнению: {prepared.report.counts.filled_fields} · Стоек: {prepared.report.counts.changed_racks}</strong>
            <span>Сохранено без изменения · полей: {prepared.report.counts.preserved_fields}</span><span>Источник: введено оператором</span>
            {geometry && <strong>Перемещений: {geometry.changes.length} · Рядов с новыми опорными точками: {Object.keys(geometry.row_updates).length}</strong>}</div>
          {geometry && snapshot && <section className="fill-fields-geometry" aria-label="Проверка размещения">
            <div className="fill-fields-map-tools"><div role="group" aria-label="Состояние плана">
              <button disabled={busy} aria-pressed={side === 'before'} onClick={() => setSide('before')}>До</button>
              <button disabled={busy} aria-pressed={side === 'after'} onClick={() => setSide('after')}>После</button>
            </div><button disabled={busy} aria-label="Показать весь зал" title="Показать весь зал" onClick={() => setFrame(frameFor(objects, snapshot.floor.settings.floorPlan))}><Maximize size={18} /></button>
            <label><input type="checkbox" disabled={busy} checked={pathway} onChange={event => setPathway(event.target.checked)} />Магистрали</label></div>
            <FloorReviewMap snapshot={snapshot} objects={objects} title={side === 'before' ? 'До' : 'После'} frame={frame}
              changes={mapChanges} selected={activeObject} onSelect={setActiveObject} onCamera={setFrame}
              layers={{ grid: true, labels: true, pathway, planning: true }} />
            <p className="fill-fields-boundary">Исходные неизвестные размеры — оценка, не измерение. Новые размеры введены оператором. Коридоры и магистрали не перемещаются.</p>
            {!!geometry.issues.length && <div role="alert" className="fill-fields-error"><strong>Размещение не подтверждено</strong><ul>{geometry.issues.map(issue => <li key={issue}>{issue}</li>)}</ul></div>}
            {geometry.can_apply && <p className="fill-fields-success" role="status"><Check size={16} />{objects.some(object => object.kind === 'rack') ? 'Границы и коллизии проверены' : 'Размеры инвентаря подтверждены · размещение не задано'}</p>}
            <div className="fill-fields-movements" aria-label="Перемещения стоек"><h3>Перемещения · {geometry.changes.length}</h3>
              {!geometry.changes.length && <p>Координаты стоек не меняются.</p>}
              {geometry.changes.map(move => <section key={move.rack_id}><button disabled={busy} onClick={() => setActiveObject('rack:' + move.rack_id)}>{move.label}</button>
                <span>{prepared.request.rack_ids.includes(move.rack_id) ? 'Выбрана' : 'Не выбрана · сосед в ряду'}</span>
                <div><span>До · X {move.before.x_m} · Y {move.before.y_m} м</span><strong>После · X {move.after.x_m} · Y {move.after.y_m} м</strong></div>
              </section>)}
            </div>
          </section>}
          <div className="fill-fields-changes">{prepared.report.changes.map(change => <section key={change.rack_id} aria-label={'Заполнение: ' + change.label}>
            <h3>{change.label} <small>ID {change.rack_id}</small></h3>
            {geometry?.estimates.filter(item => item.rack_id === change.rack_id).map(item => <p key={item.rack_id}>Исходный контур (оценка): W {item.width_m} × D {item.depth_m} м. Физические размеры не заданы.</p>)}
            {fields.filter(field => change.after[field.key] !== undefined).map(field => <div key={field.key}><span>{field.label}</span><span>Не задано</span><strong>{change.after[field.key]} {field.unit}</strong></div>)}
            {fields.filter(field => change.preserved[field.key] !== undefined).map(field => <p key={field.key}>{field.label}: {change.preserved[field.key]} {field.unit} · без изменения</p>)}
          </section>)}</div>
          {geometry && !!Object.keys(geometry.row_updates).length && <details className="fill-fields-anchors"><summary>Опорные точки рядов · {Object.keys(geometry.row_updates).length}</summary>
            {Object.entries(geometry.row_updates).map(([id, row]) => <section key={id}><h3>{row.name || id}</h3>
              <p>Начало: {valueText(row.head)} · Конец: {valueText(row.tail)}</p></section>)}
          </details>}
        </>}
        <p className="fill-fields-boundary">{geometry ? 'Размеры и перечисленные перемещения применяются вместе. U не меняется. Опубликованный план остаётся прежним.' : !prepared && (input.width_m || input.depth_m) ? 'Перед заполнением размеров будут проверены связанные перемещения, границы и коллизии. Известные значения сохраняются.' : 'Основание, размещение и U не изменяются. Опубликованный план остаётся прежним.'}</p>
        {error && <p role="alert" className="fill-fields-error">{error}</p>}
      </main>
      <footer><button disabled={busy} onClick={() => prepared ? setPrepared(null) : onClose()}><ArrowLeft size={16} />{prepared ? 'К вводу' : 'Отмена'}</button>
        {prepared ? <button className="fill-fields-primary" disabled={busy || !prepared.report.counts.filled_fields || geometry?.can_apply === false} onClick={apply}><Check size={16} />{busy ? 'Применение…' : 'Заполнить в черновике'}</button>
          : <button className="fill-fields-primary" disabled={busy || !selected.length || !Object.values(input).some(value => value.trim())} onClick={preview}><RefreshCw size={16} />{busy ? 'Проверка…' : 'Проверить заполнение'}</button>}
      </footer>
    </section>
  </div>, document.body)
}
